import {useMemo} from "react";
import {isFunction, isNil} from "lodash";
import {useTranslation} from "react-i18next";

const emptyFun = () => null;

/**
 * @param _data {Array}
 * @param _headers {Array}
 * @param _isVertical {boolean}
 * @param _onCellClick {function}
 * @param _getCellClassName {function}
 * @param _bottomPinnedData {Array|null}
 * @return {[*|[{field: string, name: string, frozen: boolean},...*],*|*[],*|(function(*, *): (*|undefined)),*|(function(*, *): (*|undefined))]}
 */
const useTableData = (_data, _headers, _isVertical, _onCellClick, _getCellClassName, _bottomPinnedData) => {

    const isHorizontal = !_isVertical;

    const {t} = useTranslation();

    const [headers, data, getRowDataByVerticalField] = useMemo(() => {
        // jeśli komponent jest horyzontalny to nic nie musimy zmieniać
        if (isHorizontal) {
            return [_headers, _data, emptyFun];
        }

        // komponent wertykalny: transpozycja danych zamieniamy wiersze z kolumnami
        // zawsze mamy JEDEN główny klucz, po którym grupujemy, wyszukamy go po polu `field` równemu `name`
        const mainHeader = _headers.find((h) => h.field === "name");

        // nowe kolumny składają się z nazwy parametru, podsumowania*, unikalnych wartości dla głównego klucza
        const newHeaders = [
            {
                name: t("parameter"),
                field: `name`,
                frozen: true
            },
            ...((!!_bottomPinnedData?.length) ? [{
                name: t("summary"),
                field: "summary",
                frozen: true
            }] : []),
            ..._data.map((d) => {
                return ({
                    name: d[mainHeader.field],
                    field: `data-${d.name}`,
                    headerComponent: mainHeader.component // Umożliwia sformatowanie header-ów (np. link do zwierzęcia)
                })
            })];

        // mapowanie wertykalnych danych na horyzontalne, użyteczne, żeby używać props-y od starych horyzontalnych komponentów
        let isMappingReady = false;
        let fieldToRowMapping = {};

        // tworzenie nowych danych
        const newData = [];
        for (const h of _headers) {
            if (h.field === mainHeader.field) continue;
            const row = {name: h.name, headerRef: h};
            _data.forEach((d) => {
                // unikalny klucz dla danego rekordu danych
                const key = `data-${d.name}`;
                row[key] = d[h.field];
                if (!isMappingReady) {
                    fieldToRowMapping[key] = d;
                }
            });
            // wstaw dane z podsumowania
            const summary = _bottomPinnedData?.[0]?.[h.field];
            if (!isNil(summary)) {
                row.summary = summary;
            }
            isMappingReady = true;
            newData.push(row);
        }
        return [newHeaders, newData, (key) => fieldToRowMapping[key]];
    }, [isHorizontal, _headers, _data, _bottomPinnedData, t]);

    const onCellClick = useMemo(() => {
        if (isHorizontal) return _onCellClick;
        return (row, header) => {
            if (isFunction(_onCellClick) && !row.frozen && !row.isIndex) {
                const row = getRowDataByVerticalField(header.field);
                if (row) {
                    return _onCellClick(row);
                }
            }
        }
    }, [_onCellClick, isHorizontal, getRowDataByVerticalField]);

    const getCellClassName = useMemo(() => {
        if (isHorizontal) return _getCellClassName;
        // musimy oszukać, że zwracamy dane z "row-a"
        return (row, header) => {
            if (isFunction(_getCellClassName) && !row.frozen && !row.isIndex) {
                const currentRow = getRowDataByVerticalField(header.field);
                if (currentRow) {
                    return _getCellClassName(currentRow);
                }
            }
        }
    }, [_getCellClassName, getRowDataByVerticalField, isHorizontal]);

    return [headers, data, onCellClick, getCellClassName];
}

export default useTableData;